import { Alert, Card, Descriptions, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import { MonoValue } from 'src/components'

import { parseAllowedScopes } from './scopeModel'
import { useAgentConnections } from './useAgentConnections'

const { Text } = Typography

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
}

/**
 * Read-only overview of how the agent authenticates. Under the shipped
 * delegated-only model there is no per-agent client / secret / TTL — the agent
 * acts on behalf of the requesting user via the workspace's shared Core OAuth
 * client, so this tab explains that rather than exposing token machinery.
 */
export const AgentOverviewTab = ({ workspaceId, agent }: Props) => {
  const { t } = useTranslation()
  // Scopes are now per connection — count the distinct codes granted across all
  // of this agent's attached MCP connections (not the legacy agent-level field).
  const { connections } = useAgentConnections(workspaceId, agent.id)
  const grantedCodes = new Set<string>()
  connections.forEach((c) =>
    c.links.forEach((rl) =>
      parseAllowedScopes(rl.link.allowed_scopes).forEach((code) =>
        grantedCodes.add(code)
      )
    )
  )
  const scopeCount = grantedCodes.size

  return (
    <Flex vertical gap={14}>
      <Card title={t('workspace.agents.overview.identityTitle')}>
        <Flex vertical gap={12}>
          <Alert
            type="info"
            showIcon
            message={t('workspace.agents.overview.identityBody', {
              name: agent.name
            })}
          />
          <Descriptions column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label={t('workspace.agents.overview.agentId')}>
              <MonoValue ellipsis>{agent.id}</MonoValue>
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.agents.overview.workspaceClient')}
            >
              <MonoValue copyable={false}>{`org_${workspaceId}`}</MonoValue>
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.agents.overview.scopeCeiling')}
            >
              {t('workspace.agents.detail.hero.scopesCount', {
                count: scopeCount
              })}
            </Descriptions.Item>
            {agent.provider ? (
              <Descriptions.Item
                label={t('workspace.agents.overview.provider')}
              >
                {agent.provider}
                {agent.model ? ` · ${agent.model}` : ''}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item
              label={t('workspace.agents.overview.toolExecutionMode')}
            >
              {t(
                `workspace.agents.toolExecution.${agent.toolExecutionMode ?? 'ask'}`,
                { defaultValue: agent.toolExecutionMode ?? 'ask' }
              )}
            </Descriptions.Item>
          </Descriptions>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.agents.overview.identityHint')}
          </Text>
        </Flex>
      </Card>
    </Flex>
  )
}
